import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LucideIcon } from "lucide-react";
import MainMenu from "./MainMenu";
import { userData } from "../../types/newtypes";

interface MobileMenuProps {
  navLinks: {
    to: string;
    label: string;
    Icon: React.ComponentType<{ height: string; width: string; fill?: string }> | null;
    LucideIcon?: LucideIcon;
  }[];
  currentUser?: userData | null;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ navLinks, currentUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent scrolling when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="lg:hidden">
      <button
        onClick={toggleMenu}
        className="p-2 transition-colors duration-200 rounded-lg hover:bg-gray-100"
        aria-label="Toggle menu"
      >
        {isOpen ? (
          <X size={24} className="text-gray-700" />
        ) : (
          <Menu size={24} className="text-gray-700" />
        )}
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="absolute top-[88px] right-4 w-64 bg-white shadow-xl z-50 rounded-lg transform transition-transform duration-300 ease-in-out"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 space-y-2">
              {navLinks.map(({ to, label, Icon, LucideIcon: LucideIconComponent }) => (
                <Link
                  key={to}
                  to={to}
                  className="flex items-center gap-3 px-3 py-2 transition-colors duration-200 rounded-lg hover:bg-gray-50"
                >
                  {Icon ? (
                    <Icon
                      height="20"
                      width="20"
                      fill={location.pathname === to ? undefined : ""}
                    />
                  ) : LucideIconComponent ? (
                    <LucideIconComponent
                      size={20}
                      className={location.pathname === to ? "text-primary" : "text-gray-500"}
                    />
                  ) : null}
                  <span className="font-medium">{label}</span>
                </Link>
              ))}

              <div className="pt-2 mt-2 border-t border-gray-200">
                <MainMenu isMobile={true} currentUser={currentUser} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileMenu;
